import { defineStore, storeToRefs } from 'pinia';
import View from 'epubjs/types/managers/view';
import * as Hammer from 'hammerjs';
import { ref, watchEffect } from 'vue';
import useReaderStore from './reader';
import useSettingStore from './setting';
import { getRenderedBookViewerElement } from '../util/book';
import useRenditionStore from './rendition';
import useUiStore, { MAX_ZOOM_FACTOR, MIN_ZOOM_FACTOR } from './ui';
import useNavigationTriggerStore from './navigation-trigger';
import { getViewBodyElement } from '../util/iframe-utils';
import { selectSentenceAtPoint } from '../util/epubJs/sentence-select';
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';

const isDefaultPinchToZoomEvent = (event: Event) => {
  // @ts-ignore
  return event.scale !== undefined && event.scale !== 1;
};

const preventDefaultPinchToZoomEvent = (event: Event) => {
  if (isDefaultPinchToZoomEvent(event)) {
    event.preventDefault();
  }
};

const isOneFingerTouch = (event: TouchEvent) => event.touches.length === 1;

const isTwoFingersOrMoreTouch = (event: TouchEvent) => event.touches.length >= 2;

const disablePinchZoomOnHtmlElement = (element: HTMLElement) => {
  element.style.touchAction = 'none';
};

/*
 isOnRightSideOfThePlayer() only exists on FlippingIframeView. The page turn animation setting is meant to imply
 that view, but the setting and the rendered view are derived from separate conditions, so we ask the view itself
 instead of trusting the setting. If they ever disagree we skip the flipping branch rather than throw on every touch.
 */
const supportsPageFlipping = (view: View) =>
  // @ts-ignore Only FlippingIframeView has this method
  typeof view?.isOnRightSideOfThePlayer === 'function';

type Point = {
  x: number;
  y: number;
};

const getDistanceBetweenPoints = (points: Point[]) => {
  if (points.length < 2) {
    return 0;
  }

  return Math.sqrt((points[0].x - points[1].x) ** 2 + (points[0].y - points[1].y) ** 2);
};

const getMiddleBetweenPoints = (points: Point[]): Point => {
  let xSum = 0;
  let ySum = 0;
  const numberOfPoints = points.length;
  for (let pointIndex = 0; pointIndex < numberOfPoints; pointIndex++) {
    xSum += points[pointIndex].x;
    ySum += points[pointIndex].y;
  }

  return {
    x: xSum / numberOfPoints,
    y: ySum / numberOfPoints,
  };
};

const getScreenTouchCenterPoint = (event: TouchEvent): Point => {
  const touchPoints: Point[] = [];
  const numberOfTouches = event.touches.length;
  for (let touchIndex = 0; touchIndex < numberOfTouches; touchIndex++) {
    touchPoints.push({ x: event.touches[touchIndex].screenX, y: event.touches[touchIndex].screenY });
  }

  return getMiddleBetweenPoints(touchPoints);
};

const fractionOfPageThatIsSwipeable = 0.66;

const isEventInSwipeableArea = (event: TouchEvent, element: HTMLElement, draggingDirection: 'LEFT' | 'RIGHT') => {
  const rectangle = element.getBoundingClientRect();
  const touchX = event.touches[0].clientX;

  if (draggingDirection === 'LEFT') {
    // We are dragging to left, so we need to swipe from the right side of the screen
    return touchX >= rectangle.left + rectangle.width * (1 - fractionOfPageThatIsSwipeable);
  }

  // We are dragging to right, so we need to swipe from the left side of the screen
  return touchX <= rectangle.left + rectangle.width * fractionOfPageThatIsSwipeable;
};

const swipeStartedOnSwipableArea = (event: HammerInput, element: HTMLElement) => {
  const rectangle = element.getBoundingClientRect();
  const touchStartX = event.center.x - event.deltaX;
  const direction = event.direction;

  if (direction === Hammer.DIRECTION_LEFT) {
    // We are swiping to left, so we need to swipe from the right side of the screen
    return touchStartX >= rectangle.left + rectangle.width * (1 - fractionOfPageThatIsSwipeable);
  }

  // We are swiping to right, so we need to swipe from the left side of the screen
  return touchStartX <= rectangle.left + rectangle.width * fractionOfPageThatIsSwipeable;
};

export const useTouchControls = (goToNextHandler: () => void, goToPreviousHandler: () => void) =>
  defineStore('touch-controls-store', () => {
    const settingsStore = useSettingStore();
    const { isTouchControlsEnabled, isPageTurnAnimationEnabled, isReflowablePageSlideAnimationEnabled } =
      storeToRefs(settingsStore);
    const readerStore = useReaderStore();
    const { isPaginated, hasHighlights } = storeToRefs(readerStore);
    const { rendition, isRTL, isReflowable } = storeToRefs(useRenditionStore());
    const uiStore = useUiStore();
    const { transformPlayer } = uiStore;
    const {
      bookPlayerZoomFactor,
      isPlayerZoomed,
      playerXDragOffset,
      playerYDragOffset,
      isAnnotationBarVisible,
      isSelectingText,
    } = storeToRefs(uiStore);
    const { setNavigationTrigger } = useNavigationTriggerStore();

    const pinchStartClientPoints = ref<Point[]>([]);
    const pinchStartScreenPoints = ref<Point[]>([]);
    const touchStartCenterScreenPoint = ref<Point>({ x: 0, y: 0 });
    const numberOfTouchesAtStart = ref(0);
    const scaleAtStartOfPinch = ref(1);
    const translationXAtStartOfTouch = ref(0);
    const translationYAtStartOfTouch = ref(0);
    const scalingFactor = ref(1);
    const translationDueToScalingX = ref(0);
    const translationDueToScalingY = ref(0);
    const translationDueToPanningX = ref(0);
    const translationDueToPanningY = ref(0);

    /**
     * Getting the coordinates as the center of the player before it is scaled.
     *
     * We assume that the pages content are placed next to each other.
     */
    const getPlayerCenterCoordinates = () => {
      if (!rendition.value?.manager) {
        return { x: 0, y: 0 };
      }
      const visibleViews: View[] = rendition.value.manager.visible();
      if (!visibleViews.length) {
        return {
          x: 0,
          y: 0,
        };
      }

      const firstView = visibleViews[0];

      /*
      Check how many pages would be rendered on the screen. Because sometimes we display only one (cover or last page)
      but the player should still be zoomed from the center of the screen.

      Reflowable books are special because they only display one iframe, with one body element but are still configured
      with a 2-page-layout
       */
      // @ts-ignore
      const numberOfPagesOnScreen: number = firstView.layout.divisor;

      let bookWidth = 0;
      let bookHeight = 0;
      const bodyElement = getViewBodyElement(firstView)!;
      const rectangle = bodyElement.getBoundingClientRect();

      bookWidth = rectangle.width * (isReflowable.value ? 1 : numberOfPagesOnScreen);

      // To keep in mind - This would not work for continous books, but for now we don't have touch controls for them
      bookHeight = rectangle.height;

      /*
   Known limitation - does not work for reflowable books. For reflowable books we always zoom from the center
    */
      if (isReflowable.value) {
        return {
          x: firstView.width().valueOf() / 2,
          y: firstView.height().valueOf() / 2,
        };
      }

      // TODO - test for reflowable books. Because the body element of reflowable books might be larger than the screen.
      return {
        x: bookWidth / 2,
        y: bookHeight / 2,
      };
    };

    const getEventCoordinatesRelativeToOriginalScalePlayer = (
      view: View,
      clickXrelativeToIframe: number,
      clickYrelativeToIframe: number,
    ) => {
      /*
     Click is triggered in the content (body) of the iframe, so we need handle it as if it was one big element.

     If we click on the left page we need to consider that there might be a left margin.
      */

      /*
      Known limitation - does not work for reflowable books.
      Reflowable books are displayed in one big iframe. But sometimes(cover) we have it placed on the right
       */
      if (isReflowable.value) {
        return {
          x: view.width().valueOf() / 2,
          y: clickYrelativeToIframe,
        };
      }

      // @ts-ignore
      const isRightSidePage: boolean = view.layout.divisor === 2 && view.index % 2 === (isRTL.value ? 1 : 0);
      // @ts-ignore
      const bodyElement = getViewBodyElement(view)!;
      const rectangle = bodyElement.getBoundingClientRect();

      if (isRightSidePage) {
        /* If the page is on the right side we need to consider the width of the left page
         We assume the right and left page have the same width
         */
        return {
          x: clickXrelativeToIframe + rectangle.width,
          y: clickYrelativeToIframe,
        };
      }

      return {
        x: clickXrelativeToIframe - rectangle.left,
        y: clickYrelativeToIframe - rectangle.top,
      };
    };

    const registerViewerElementTouchHandlers = () => {
      if (!isTouchControlsEnabled.value) {
        return;
      }

      const viewerElement = getRenderedBookViewerElement();
      if (!viewerElement) {
        return;
      }

      viewerElement.addEventListener(
        'touchmove',
        (event) => {
          preventDefaultPinchToZoomEvent(event);
        },
        { passive: false },
      );
    };

    const isAnimatingPageTurn = () => {
      const manager = rendition.value?.manager;
      // ReflowableSliderManager: covers the whole turn, including loading the chapter next to the current one.
      if (isReflowablePageSlideAnimationEnabled.value && manager?.isTurningPage) {
        return true;
      }
      if (!isPageTurnAnimationEnabled.value || !manager) {
        return false;
      }
      // We know we have a flipping manager
      if (manager && typeof manager.hasAnimationInProgress === 'function') {
        return manager.hasAnimationInProgress();
      }
      return false;
    };

    const updatePlayerTransformation = () => {
      requestAnimationFrame(() => {
        const xOffset =
          translationXAtStartOfTouch.value + translationDueToScalingX.value + translationDueToPanningX.value;
        const yOffset =
          translationYAtStartOfTouch.value + translationDueToScalingY.value + translationDueToPanningY.value;
        transformPlayer(scalingFactor.value, xOffset, yOffset);
      });
    };

    const registerBookPageTouchHandlers = (view: View) => {
      if (!isTouchControlsEnabled.value) {
        return;
      }

      /*
    The page element (the iframe container) can be bigger than the rendered page,
    so we need to disable pinch zoom on the page element also
     */
      // @ts-ignore
      const pageElement = view.element as HTMLElement;
      disablePinchZoomOnHtmlElement(pageElement);

      const bodyElement = getViewBodyElement(view);
      if (!bodyElement) {
        return;
      }

      disablePinchZoomOnHtmlElement(bodyElement);

      // Inject a stylesheet into the iframe doc to force user-select: text on all elements.
      // Epub CSS can set user-select: none on spans; our body inline style doesn't override that.
      // We own all touch gestures via HammerJS + preventDefault so native long-press selection
      // is already blocked — enabling user-select here only affects programmatic selections.
      const iframeDoc = bodyElement.ownerDocument;
      if (iframeDoc && !iframeDoc.getElementById('epub-sel-override')) {
        const selStyle = iframeDoc.createElement('style');
        selStyle.id = 'epub-sel-override';
        selStyle.textContent = '* { -webkit-user-select: text !important; user-select: text !important; }';
        iframeDoc.head?.appendChild(selStyle);
      }

      // @ts-ignore - Hammer is available globally
      const hammertime = new window.Hammer(bodyElement);
      hammertime.get('swipe').set({ enable: true });
      hammertime.get('pan').set({ enable: false });
      hammertime.get('swipe').set({ direction: Hammer.DIRECTION_HORIZONTAL });
      hammertime.get('press').set({ enable: true, time: 500, threshold: 10 });

      hammertime.on('swipe', (event: HammerInput) => {
        // Ignore mouse-initiated drags — only touch/pen swipes should turn pages.
        // Without this, click-dragging to select text fires a swipe and navigates.
        if (event.pointerType === 'mouse') return;
        if (isAnnotationBarVisible.value || isSelectingText.value) return;
        if (!isPaginated.value || isPlayerZoomed.value) {
          return;
        }

        const isLeftSwipe = event.direction === Hammer.DIRECTION_LEFT;

        setNavigationTrigger(isLeftSwipe ? 'SWIPE_LEFT' : 'SWIPE_RIGHT');

        if (isPageTurnAnimationEnabled.value && supportsPageFlipping(view)) {
          if (swipeStartedOnSwipableArea(event, bodyElement)) {
            // @ts-ignore
            const isRightSidePage: boolean = view.isOnRightSideOfThePlayer();

            // In RTL books, the page sides and swipe directions are inverted
            if (isRTL.value) {
              // RTL: Left page + right swipe = forward, Right page + left swipe = backward
              if (isRightSidePage && !isLeftSwipe) {
                goToNextHandler();
              } else if (!isRightSidePage && isLeftSwipe) {
                goToPreviousHandler();
              }
            } else {
              // LTR: Right page + left swipe = forward, Left page + right swipe = backward
              if (isRightSidePage && isLeftSwipe) {
                goToNextHandler();
              } else if (!isRightSidePage && !isLeftSwipe) {
                goToPreviousHandler();
              }
            }
          }
        } else if (isLeftSwipe) {
          // In RTL books, left swipe should go backward
          if (isRTL.value) {
            goToPreviousHandler();
          } else {
            goToNextHandler();
          }
        } else {
          // In RTL books, right swipe should go forward
          if (isRTL.value) {
            goToNextHandler();
          } else {
            goToPreviousHandler();
          }
        }
      });
      hammertime.on('tap', (event: HammerInput) => {
        /*
          We use the tap event to replicate the click event that we disabled below in the touchstart handler.
           */
        const targetElement = event.srcEvent.target as HTMLElement;
        if (targetElement) {
          targetElement.click();
        }
      });

      hammertime.on('press', async (event: HammerInput) => {
        // Mouse hold should not trigger sentence selection — only touch long-press.
        if (event.pointerType === 'mouse') return;
        if (!isTouchControlsEnabled.value || !hasHighlights.value) return;

        // Prevent touchend (fired right after press) from clearing the programmatic selection
        event.srcEvent.preventDefault();

        const iframeRect = bodyElement.getBoundingClientRect();
        const localX = event.center.x - iframeRect.left;
        const localY = event.center.y - iframeRect.top;

        const range = selectSentenceAtPoint(iframeDoc, localX, localY);
        if (!range || range.collapsed) return;

        const iframeWindow = iframeDoc.defaultView;
        if (!iframeWindow) return;

        // Focus the iframe element then its body — both needed for Chrome to render
        // the selection highlight visually inside an about:srcdoc iframe.
        const iframeEl = iframeWindow.frameElement as HTMLElement | null;
        iframeEl?.focus({ preventScroll: true });
        iframeDoc.body.setAttribute('tabindex', '-1');
        iframeDoc.body.focus({ preventScroll: true });

        const sel = iframeWindow.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(range);
          // Set both flags synchronously — the watch(showMenu) watcher is async (Vue flush)
          // so without this, a swipe in the same gesture fires before isAnnotationBarVisible=true
          // and bypasses the navigation guard.
          isAnnotationBarVisible.value = true;
          document.documentElement.style.touchAction = 'auto';
        }

        if (Capacitor.isNativePlatform()) {
          try {
            await Haptics.impact({ style: ImpactStyle.Medium });
          } catch {
            // Haptics not available on this device/simulator
          }
        }
      });

      bodyElement.addEventListener(
        'touchstart',
        (event) => {
          // Let native touch through when annotation bar is open so taps on the page aren't eaten.
          if (isAnnotationBarVisible.value) return;

          /*
        We preventDefault() in order not to trigger the click event when zooming, which can have other
        behaviours  (e.g. playing an element's audio).

        But we replicate the click in the tap event of HammerJS
         */
          event.preventDefault();

          translationXAtStartOfTouch.value = playerXDragOffset.value;
          translationYAtStartOfTouch.value = playerYDragOffset.value;
          touchStartCenterScreenPoint.value = getScreenTouchCenterPoint(event);
          numberOfTouchesAtStart.value = event.touches.length;

          if (isTwoFingersOrMoreTouch(event)) {
            pinchStartClientPoints.value = [
              { x: event.touches[0].clientX, y: event.touches[0].clientY },
              { x: event.touches[1].clientX, y: event.touches[1].clientY },
            ];

            pinchStartScreenPoints.value = [
              { x: event.touches[0].screenX, y: event.touches[0].screenY },
              { x: event.touches[1].screenX, y: event.touches[1].screenY },
            ];

            scaleAtStartOfPinch.value = bookPlayerZoomFactor.value;
            translationDueToScalingX.value = 0;
            translationDueToScalingY.value = 0;
          }

          if (
            isPageTurnAnimationEnabled.value &&
            supportsPageFlipping(view) &&
            isOneFingerTouch(event) &&
            !isPlayerZoomed.value &&
            rendition.value
          ) {
            /*
          We know that if the page turn animation is enabled, we use a flipping iframe and a flipping manager
           */
            // @ts-ignore
            const draggingDirection = view.isOnRightSideOfThePlayer() ? 'LEFT' : 'RIGHT';

            if (isEventInSwipeableArea(event, bodyElement, draggingDirection)) {
              // @ts-ignore
              rendition.value.manager.registerPageDragDirection(draggingDirection);
            }
          }
        },
        {
          passive: false,
        },
      );

      bodyElement.addEventListener(
        'touchmove',
        (event) => {
          // Let native selection handles move freely when annotation bar is open
          if (isAnnotationBarVisible.value) return;

          if (isPageTurnAnimationEnabled.value && isOneFingerTouch(event) && !isPlayerZoomed.value && rendition.value) {
            // @ts-ignore
            rendition.value.manager.moveDraggedPage(event);
          }

          if (isTwoFingersOrMoreTouch(event) && !isAnimatingPageTurn()) {
            const currentScreenPoints = [
              { x: event.touches[0].screenX, y: event.touches[0].screenY },
              { x: event.touches[1].screenX, y: event.touches[1].screenY },
            ];

            const startDistanceBetweenFingers = getDistanceBetweenPoints(pinchStartScreenPoints.value);
            const currentDistanceBetweenFingers = getDistanceBetweenPoints(currentScreenPoints);
            const notLimitedScaleMultiplier = currentDistanceBetweenFingers / startDistanceBetweenFingers;

            /*
          We limit the scale multiplier with the min and max zoom factors
           */
            const notLimitedFutureScale = scaleAtStartOfPinch.value * notLimitedScaleMultiplier;
            const limitedFutureScale = Math.min(MAX_ZOOM_FACTOR, Math.max(MIN_ZOOM_FACTOR, notLimitedFutureScale));
            scalingFactor.value = limitedFutureScale;
            // TODO - as we only use values from the beginning of pinch here, maybe this should be done at touchstart

            const centerOfPinchStart = getMiddleBetweenPoints(pinchStartClientPoints.value);
            const eventCoordinatesRelativeToOriginalScalePlayer = getEventCoordinatesRelativeToOriginalScalePlayer(
              view,
              centerOfPinchStart.x,
              centerOfPinchStart.y,
            );

            /*
          When zooming the player, we need to translate it so that the zoomed point stays in the same place.
          The translation is relative to the center of the player because the viewer has transform-origin: center.
          The player has transform-origin:center because the center is the only point that overlaps with between the
          viewer element and the actual book.
            */
            const playerCenterCoordinates = getPlayerCenterCoordinates();

            translationDueToScalingX.value =
              (playerCenterCoordinates.x - eventCoordinatesRelativeToOriginalScalePlayer.x) *
              (limitedFutureScale - scaleAtStartOfPinch.value);
            translationDueToScalingY.value =
              (playerCenterCoordinates.y - eventCoordinatesRelativeToOriginalScalePlayer.y) *
              (limitedFutureScale - scaleAtStartOfPinch.value);

            updatePlayerTransformation();
          } else {
            // We don't have two fingers on the screen. Resetting scaling translation
            translationDueToScalingX.value = 0;
            translationDueToScalingY.value = 0;
          }

          /*
            We only allow dragging if the player is zoomed in
            and the number of touches is the same as at the start of the touch event,
           meaning that the user did not lift a finger after the touch start.
           Otherwise, we can not calculate the drag distance, because the center of the touches changes - resulting in a sudden flicker.
           */
          if (isPlayerZoomed.value && event.touches.length === numberOfTouchesAtStart.value) {
            const currentTouchCenterScreenPoint = getScreenTouchCenterPoint(event);

            const xDrag = currentTouchCenterScreenPoint.x - touchStartCenterScreenPoint.value.x;
            const yDrag = currentTouchCenterScreenPoint.y - touchStartCenterScreenPoint.value.y;

            translationDueToPanningX.value = xDrag;
            translationDueToPanningY.value = yDrag;
            updatePlayerTransformation();
          }
        },
        {
          passive: false,
        },
      );
      bodyElement.addEventListener('touchend', (_event) => {
        if (isPageTurnAnimationEnabled.value && rendition.value) {
          // @ts-ignore
          rendition.value.manager.onPageDragEnd();
        }
      });

      bodyElement.addEventListener('touchcancel', (_event) => {
        if (isPageTurnAnimationEnabled.value && rendition.value) {
          // @ts-ignore
          rendition.value.manager.onPageDragEnd();
        }
      });
    };

    const disableAppPinchZoom = () => {
      if (!isTouchControlsEnabled.value) {
        return;
      }
      disablePinchZoomOnHtmlElement(document.documentElement);
    };

    const enableAppPinchToZoom = () => {
      if (!isTouchControlsEnabled.value) {
        return;
      }

      document.documentElement.style.touchAction = 'auto';
    };

    // Lift touch-action:none when the annotation bar is open so native selection handles work.
    // Owned here because touch-controls sets the initial touch-action.
    watchEffect(() => {
      if (!isTouchControlsEnabled.value) return;
      document.documentElement.style.touchAction = isAnnotationBarVisible.value ? 'auto' : 'none';
    });

    return {
      registerViewerElementTouchHandlers,
      registerBookPageTouchHandlers,
      disableAppPinchZoom,
      enableAppPinchToZoom,
    };
  })();
